import { useState } from 'react'
import axios from 'axios'
import Turnstile from './Turnstile'

const GATE_ENABLED = import.meta.env.VITE_SITE_GATE === 'true'
const STORAGE_KEY  = 'verse_unlocked'

export default function PasswordGate({ children }) {
  const [unlocked, setUnlocked] = useState(
    () => localStorage.getItem(STORAGE_KEY) === 'true'
  )
  const [input, setInput]     = useState('')
  const [error, setError]     = useState(false)
  const [shake, setShake]     = useState(false)
  const [loading, setLoading] = useState(false)
  const [captchaToken, setCaptchaToken] = useState(null)

  // If the gate is not enabled, don't gate anything
  if (!GATE_ENABLED) return children

  if (unlocked) return children

  async function handleSubmit(e) {
    e.preventDefault()
    setLoading(true)
    try {
      await axios.post('/api/auth/site-password', { password: input, turnstile_token: captchaToken })
      localStorage.setItem(STORAGE_KEY, 'true')
      setUnlocked(true)
    } catch {
      setError(true)
      setShake(true)
      setInput('')
      setTimeout(() => setShake(false), 600)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div style={styles.overlay}>
      <div style={{ ...styles.card, animation: shake ? 'pg-shake 0.5s ease' : 'none' }}>
        <div style={styles.logo}>
          <span style={styles.logoText}>verse</span>
        </div>
        <h1 style={styles.heading}>Coming soon</h1>
        <p style={styles.sub}>Enter the preview password to access the site.</p>
        <form onSubmit={handleSubmit} style={styles.form}>
          <input
            type="password"
            value={input}
            onChange={e => { setInput(e.target.value); setError(false) }}
            placeholder="Password"
            autoFocus
            style={{ ...styles.input, ...(error ? styles.inputError : {}) }}
          />
          {error && <p style={styles.error}>Incorrect password</p>}
          <Turnstile onToken={setCaptchaToken} onExpire={() => setCaptchaToken(null)} />
          <button type="submit" disabled={loading} style={styles.btn}>
            {loading ? 'Checking…' : 'Enter'}
          </button>
        </form>
      </div>
      <style>{`
        @keyframes pg-shake {
          0%, 100% { transform: translateX(0); }
          20%       { transform: translateX(-8px); }
          40%       { transform: translateX(8px); }
          60%       { transform: translateX(-6px); }
          80%       { transform: translateX(6px); }
        }
      `}</style>
    </div>
  )
}

const styles = {
  overlay: {
    minHeight: '100vh',
    background: '#FAF5FF',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '1rem',
  },
  card: {
    background: '#FFFFFF',
    border: '1px solid #E9D5FF',
    borderRadius: 20,
    padding: '2.5rem 2rem',
    width: '100%',
    maxWidth: 380,
    boxShadow: '0 8px 32px rgba(124,58,237,0.18)',
    textAlign: 'center',
  },
  logo: {
    marginBottom: '1.25rem',
  },
  logoText: {
    fontSize: '2rem',
    fontWeight: 800,
    background: 'linear-gradient(135deg, #7C3AED, #EC4899)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
    fontFamily: 'Inter, sans-serif',
  },
  heading: {
    fontSize: '1.4rem',
    fontWeight: 700,
    color: '#1E1B4B',
    marginBottom: '0.4rem',
  },
  sub: {
    color: '#6B7280',
    fontSize: '0.9rem',
    marginBottom: '1.5rem',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.75rem',
  },
  input: {
    padding: '0.75rem 1rem',
    borderRadius: 10,
    border: '1.5px solid #E9D5FF',
    fontSize: '1rem',
    outline: 'none',
    color: '#1E1B4B',
    background: '#FAF5FF',
    transition: 'border-color 0.2s',
  },
  inputError: {
    borderColor: '#EC4899',
  },
  error: {
    color: '#EC4899',
    fontSize: '0.82rem',
    textAlign: 'left',
    marginTop: '-0.25rem',
  },
  btn: {
    padding: '0.75rem',
    borderRadius: 10,
    border: 'none',
    background: 'linear-gradient(135deg, #7C3AED, #EC4899)',
    color: '#fff',
    fontWeight: 700,
    fontSize: '1rem',
    cursor: 'pointer',
    marginTop: '0.25rem',
  },
}
